import { useEffect, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { PageHeader } from "../../components/layout/PageHeader";
import { Modal } from "../../components/ui/Modal";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import {
  permissionGroupsApi,
  type GroupPermission,
  type PermissionGroup,
} from "../../api/permissionGroupsApi";

// Matches the app's shared `.field` input styling (see app.css).
const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "14px 16px",
  border: "2px solid var(--portal-border)",
  borderRadius: 8,
  fontSize: "1rem",
  fontFamily: "inherit",
  background: "white",
};

/**
 * Admin-only permission-group management. Master-detail: pick a group on
 * the left (create / rename / delete), toggle its permissions on the
 * right. Mirrors the legacy AdminPermissions "Permissions" screen. Groups
 * are assigned to individual logins from the customer detail page.
 */
export function PermissionGroupsPage() {
  const [groups, setGroups] = useState<PermissionGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [renaming, setRenaming] = useState<PermissionGroup | null>(null);
  const [deleting, setDeleting] = useState<PermissionGroup | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    permissionGroupsApi
      .list()
      .then((r) => {
        if (!active) return;
        setGroups(r);
        setSelectedId((prev) => prev ?? r[0]?.portalPermissionGroupId ?? null);
      })
      .catch((err) => active && setError(err instanceof Error ? err.message : "Failed to load groups"));
    return () => {
      active = false;
    };
  }, []);

  const selected = useMemo(
    () => groups?.find((g) => g.portalPermissionGroupId === selectedId) ?? null,
    [groups, selectedId],
  );

  const applyGroups = (next: PermissionGroup[]) => {
    setGroups(next);
    if (!next.some((g) => g.portalPermissionGroupId === selectedId)) {
      setSelectedId(next[0]?.portalPermissionGroupId ?? null);
    }
  };

  const remove = async (groupId: number) => {
    setBusy(true);
    try {
      applyGroups(await permissionGroupsApi.remove(groupId));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Permission groups"
        actions={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setCreating(true)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={14} /> New group
          </button>
        }
      />
      <div className="dashboard">
        {error && (
          <div className="tile" style={{ borderColor: "#e74c3c", color: "#c0392b", background: "#fdecea" }}>
            {error}
          </div>
        )}
        {groups == null ? (
          <div className="tile" style={{ color: "var(--portal-muted)" }}>Loading…</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 300px) 1fr", gap: 16 }}>
            <section className="tile" style={{ gap: 4, alignSelf: "start" }}>
              <header className="tile-head">
                <h3 style={{ margin: 0 }}>Groups</h3>
              </header>
              {groups.length === 0 ? (
                <div style={{ color: "var(--portal-muted)" }}>No groups yet.</div>
              ) : (
                groups.map((g) => (
                  <button
                    key={g.portalPermissionGroupId}
                    type="button"
                    onClick={() => setSelectedId(g.portalPermissionGroupId)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: "1px solid",
                      borderColor:
                        g.portalPermissionGroupId === selectedId
                          ? "var(--portal-primary)"
                          : "transparent",
                      background:
                        g.portalPermissionGroupId === selectedId
                          ? "var(--portal-primary-soft)"
                          : "transparent",
                      cursor: "pointer",
                      textAlign: "left",
                      font: "inherit",
                    }}
                  >
                    <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {g.name}
                    </span>
                    <span style={{ color: "var(--portal-muted)", fontSize: "0.75rem", flexShrink: 0 }}>
                      {g.permissionCount}p · {g.loginCount}l
                    </span>
                  </button>
                ))
              )}
            </section>

            {selected ? (
              <GroupPermissionsPanel
                key={selected.portalPermissionGroupId}
                group={selected}
                onRename={() => setRenaming(selected)}
                onDelete={() => setDeleting(selected)}
              />
            ) : (
              <section className="tile" style={{ color: "var(--portal-muted)" }}>
                Select or create a group to manage its permissions.
              </section>
            )}
          </div>
        )}
      </div>

      {creating && (
        <NameModal
          title="New permission group"
          initial=""
          onClose={() => setCreating(false)}
          onSubmit={async (name) => {
            applyGroups(await permissionGroupsApi.create(name));
            setCreating(false);
          }}
        />
      )}
      {renaming && (
        <NameModal
          title="Rename group"
          initial={renaming.name}
          onClose={() => setRenaming(null)}
          onSubmit={async (name) => {
            applyGroups(await permissionGroupsApi.rename(renaming.portalPermissionGroupId, name));
            setRenaming(null);
          }}
        />
      )}
      <ConfirmModal
        open={deleting != null}
        onClose={() => {
          if (!busy) setDeleting(null);
        }}
        title="Delete permission group?"
        confirmLabel="Delete"
        tone="danger"
        busy={busy}
        onConfirm={() => {
          const g = deleting;
          setDeleting(null);
          if (g) void remove(g.portalPermissionGroupId);
        }}
      >
        Delete <strong>{deleting?.name}</strong>? It will be removed from every login that has it.
      </ConfirmModal>
    </>
  );
}

function GroupPermissionsPanel({
  group,
  onRename,
  onDelete,
}: {
  group: PermissionGroup;
  onRename: () => void;
  onDelete: () => void;
}) {
  const [perms, setPerms] = useState<GroupPermission[] | null>(null);
  const [filter, setFilter] = useState("");
  const [pending, setPending] = useState<number | null>(null);

  useEffect(() => {
    let active = true;
    permissionGroupsApi
      .permissions(group.portalPermissionGroupId)
      .then((r) => active && setPerms(r))
      .catch(() => active && setPerms([]));
    return () => {
      active = false;
    };
  }, [group.portalPermissionGroupId]);

  const filtered = useMemo(() => {
    if (!perms) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return perms;
    return perms.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description ?? "").toLowerCase().includes(q),
    );
  }, [perms, filter]);

  const toggle = async (permissionId: number, assigned: boolean) => {
    setPending(permissionId);
    try {
      const next = assigned
        ? await permissionGroupsApi.removePermission(group.portalPermissionGroupId, permissionId)
        : await permissionGroupsApi.addPermission(group.portalPermissionGroupId, permissionId);
      setPerms(next);
    } finally {
      setPending(null);
    }
  };

  return (
    <section className="tile" style={{ gap: 12 }}>
      <header className="tile-head" style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {group.name}
        </h3>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onRename}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem" }}
          >
            <Pencil size={14} /> Rename
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onDelete}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: "0.8rem" }}
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      </header>

      <input
        type="text"
        value={filter}
        placeholder="Filter permissions…"
        onChange={(e) => setFilter(e.target.value)}
        className="portal-input-inline" style={inputStyle}
      />

      {perms == null ? (
        <div style={{ color: "var(--portal-muted)" }}>Loading…</div>
      ) : (
        <div style={{ display: "grid", gap: 2 }}>
          {filtered.map((p) => (
            <label
              key={p.portalPermissionId}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                alignItems: "center",
                gap: 10,
                padding: "6px 4px",
                opacity: pending === p.portalPermissionId ? 0.5 : 1,
              }}
              title={p.description}
            >
              <input
                type="checkbox"
                checked={p.assigned}
                disabled={pending != null}
                onChange={() => void toggle(p.portalPermissionId, p.assigned)}
              />
              <code style={{ fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {p.name}
              </code>
              <span style={{ color: "var(--portal-muted)", fontSize: "0.75rem" }}>{p.loginType}</span>
            </label>
          ))}
          {filtered.length === 0 && (
            <div style={{ color: "var(--portal-muted)" }}>No permissions match "{filter}".</div>
          )}
        </div>
      )}
    </section>
  );
}

function NameModal({
  title,
  initial,
  onClose,
  onSubmit,
}: {
  title: string;
  initial: string;
  onClose: () => void;
  onSubmit: (name: string) => Promise<void>;
}) {
  const [name, setName] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit(name.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => {
        if (!busy) onClose();
      }}
      title={title}
      busy={busy}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void submit()} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 10 }}>
        {error && (
          <div style={{ color: "#c0392b", background: "#fdecea", border: "1px solid #e74c3c", borderRadius: 8, padding: 10, fontSize: "0.9rem" }}>
            {error}
          </div>
        )}
        <input
          type="text"
          value={name}
          maxLength={50}
          autoFocus
          onChange={(e) => setName(e.target.value)}
          className="portal-input-inline" style={inputStyle}
        />
      </div>
    </Modal>
  );
}
